import {useMemo} from 'react';
import {useQuery} from '@apollo/client';
import {
  ContactDeliveryPreferences,
  ContactDeliveryPreferencesVariables,
} from '../../schema-types/ContactDeliveryPreferences';
import validateUUID from '../../utils/validateUUID';
import {GET_CONTACT_DELIVERY_PREFERENCES} from './deliveryPreferenceQueries';
import {DeliveryPreferences} from './deliveryPreferenceText';

/**
 * The current delivery preferences of every contact on an account, keyed by
 * contact id (DBO-1575). Its own small query, like the correspondence needs, so
 * the overview grid can look a value up without widening the shared contact
 * fragment; the update mutation returns the same fragment, so Apollo refreshes
 * the map from the mutation result.
 */
export const useContactDeliveryPreferences = (
  accountId: string | undefined,
  {enabled = true}: {enabled?: boolean} = {}
) => {
  const {data, loading, error, refetch} = useQuery<
    ContactDeliveryPreferences,
    ContactDeliveryPreferencesVariables
  >(GET_CONTACT_DELIVERY_PREFERENCES, {
    variables: {accountId: validateUUID(accountId)!},
    skip: !accountId || !enabled,
  });
  const preferences = useMemo(() => {
    const byContact: Record<string, DeliveryPreferences> = {};
    (data?.contacts ?? []).forEach((contact) => {
      byContact[contact.id] = {
        doNotEmail: contact.doNotEmail,
        doNotSms: contact.doNotSms,
      };
    });
    return byContact;
  }, [data]);
  return {preferences, loading, error, refetch};
};
